import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Search, Filter, X, Table2, ChefHat, AlertCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { MobileLayout } from "@/components/layouts/MobileLayout";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import axios from "axios";
import API_URL from "@/utils/api";

const API = API_URL;

// Key for storing table number from QR
const TABLE_QR_KEY = "app_qr_table";

export default function MenuPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(
    searchParams.get("category") || ""
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [tableFromQR, setTableFromQR] = useState(null);
  const [showTableConfirmation, setShowTableConfirmation] = useState(false);
  const [tableConfirmed, setTableConfirmed] = useState(false);
  const [changeTableNumber, setChangeTableNumber] = useState("");
  const [hasCustomTacoIngredients, setHasCustomTacoIngredients] = useState(false);

  // Check for table and branch parameter from QR code
  useEffect(() => {
    const mesa = searchParams.get("mesa");
    const sucursal = searchParams.get("sucursal");

    if (sucursal) {
      localStorage.setItem("app_qr_branch", sucursal);
    }

    if (mesa) {
      // Check if this is already confirmed
      const savedTable = localStorage.getItem(TABLE_QR_KEY);
      const confirmed = localStorage.getItem("app_table_confirmed");

      if (savedTable === mesa && confirmed === "true") {
        // Already confirmed, just set it
        setTableFromQR(mesa);
        setTableConfirmed(true);
      } else {
        // New table scan - show confirmation
        setTableFromQR(mesa);
        setShowTableConfirmation(true);
        setTableConfirmed(false);
      }
    } else {
      // Check if we have a saved table
      const savedTable = localStorage.getItem(TABLE_QR_KEY);
      const confirmed = localStorage.getItem("app_table_confirmed");
      const savedBranch = localStorage.getItem("app_qr_branch");

      if (savedTable && confirmed === "true") {
        setTableFromQR(savedTable);
        setTableConfirmed(true);

        // BUG FIX: If URL is missing params but we have them in storage, RESTORE THEM
        const hasMesaInUrl = searchParams.has("mesa");
        if (!hasMesaInUrl) {
          const newParams = new URLSearchParams(searchParams);
          newParams.set("mesa", savedTable);
          if (savedBranch) newParams.set("sucursal", savedBranch);
          navigate(`/menu?${newParams.toString()}`, { replace: true });
        }
      }
    }
  }, [searchParams, navigate]);

  // Confirm table selection
  const confirmTable = () => {
    localStorage.setItem(TABLE_QR_KEY, tableFromQR);
    localStorage.setItem("app_table_confirmed", "true");
    setTableConfirmed(true);
    setShowTableConfirmation(false);
  };

  // Change table number
  const handleChangeTable = () => {
    if (!changeTableNumber || changeTableNumber.trim() === "") return;

    const newTable = changeTableNumber.trim();
    localStorage.setItem(TABLE_QR_KEY, newTable);
    localStorage.setItem("app_table_confirmed", "true");
    setTableFromQR(newTable);
    setTableConfirmed(true);
    setShowTableConfirmation(false);
    setChangeTableNumber("");

    // Update URL
    const newParams = new URLSearchParams(searchParams);
    newParams.set("mesa", newTable);
    setSearchParams(newParams);
  };

  // Clear table selection
  const clearTableSelection = () => {
    localStorage.removeItem(TABLE_QR_KEY);
    localStorage.removeItem("app_table_confirmed");
    setTableFromQR(null);
    setTableConfirmed(false);
    setShowTableConfirmation(false);
    // Remove mesa param from URL
    const newParams = new URLSearchParams(searchParams);
    newParams.delete("mesa");
    setSearchParams(newParams);
  };


  useEffect(() => {
    const fetchData = async () => {
      try {
        // Get branch context
        const searchParams = new URLSearchParams(window.location.search);
        const urlBranch = searchParams.get("sucursal");
        const savedBranch = localStorage.getItem("app_qr_branch");
        const branchId = urlBranch || savedBranch;
        const branchQuery = branchId ? `?branch_id=${branchId}` : "";

        const [catRes, menuRes, configRes, customTacoRes] = await Promise.all([
          axios.get(`${API}/categories`),
          axios.get(`${API}/menu${branchQuery}`),
          axios.get(`${API}/config`),
          axios.get(`${API}/custom-taco/config`).catch(() => ({ data: null })),
        ]);
        const activeCategories = catRes.data.filter(c => c.active);
        setCategories(activeCategories);
        setMenuItems(menuRes.data);

        // Inject Branding Colors
        const primaryColor = configRes.data?.primary_color || configRes.data?.branding?.primary_color;
        if (primaryColor) {
           document.documentElement.style.setProperty('--primary', primaryColor);
           document.documentElement.style.setProperty('--accent', primaryColor);
        }

        // Check if custom taco has ingredients configured
        if (customTacoRes && customTacoRes.data) {
          const hasIngredients =
            (customTacoRes.data.tortillas?.length > 0) ||
            (customTacoRes.data.proteins?.length > 0) ||
            (customTacoRes.data.toppings?.length > 0) ||
            (customTacoRes.data.salsas?.length > 0);
          setHasCustomTacoIngredients(hasIngredients);
        }

        // Auto-select first category if none selected
        if (!selectedCategory && activeCategories.length > 0) {
          setSelectedCategory(activeCategories[0].category_id);
        }

        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching menu:", error);
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    let items = [...menuItems];

    if (selectedCategory) {
      // Filter directly by category_id which is cleaner
      items = items.filter((item) => item.category_id === selectedCategory);
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      items = items.filter((item) => {
        // Search in name and description
        const matchName = item.name.toLowerCase().includes(query);
        const matchDesc = item.description?.toLowerCase().includes(query);
        // Also search by category name
        const category = categories.find((c) => c.name === item.category);
        const matchCategory = category?.name.toLowerCase().includes(query);
        return matchName || matchDesc || matchCategory;
      });
    }

    setFilteredItems(items);
  }, [menuItems, selectedCategory, searchQuery, categories]);

  const handleCategoryChange = (categoryId) => {
    setSelectedCategory(categoryId);
    const newParams = new URLSearchParams();
    if (categoryId) {
      newParams.set("category", categoryId);
    }
    // Preserve mesa param
    const mesa = searchParams.get("mesa");
    if (mesa) {
      newParams.set("mesa", mesa);
    }
    setSearchParams(newParams);
  };

  const getCategoryName = (categoryId) => {
    const cat = categories.find((c) => c.category_id === categoryId);
    return cat?.name || "";
  };

  if (isLoading) {
    return (
      <MobileLayout>
        <div className="p-4 space-y-4">
          <div className="h-12 bg-gray-100 rounded-xl animate-pulse" />
          <div className="flex gap-2 overflow-hidden">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-10 w-24 bg-gray-100 rounded-full animate-pulse flex-shrink-0" />
            ))}
          </div>
          <div className="grid grid-cols-1 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-32 bg-gray-100 rounded-2xl animate-pulse" />
            ))}
          </div>
        </div>
      </MobileLayout>
    );
  }

  return (
    <MobileLayout>
      {/* Table from QR Banner - Only show when confirmed */}
      {tableFromQR && tableConfirmed && (
        <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-4 py-4 shadow-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-white/20 backdrop-blur p-2 rounded-lg">
                <Table2 className="w-6 h-6" />
              </div>
              <div>
                <p className="text-xs font-medium opacity-90">Pedido para:</p>
                <p className="font-bold text-lg">Mesa {tableFromQR}</p>
              </div>
            </div>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setShowTableConfirmation(true)}
              className="text-white hover:bg-white/20 border border-white/30"
            >
              Cambiar
            </Button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="sticky top-0 bg-background z-30 px-4 pt-4 pb-2">
        <h1 className="font-heading text-2xl font-bold mb-4">Menú</h1>

        {/* Search */}
        <div className="relative mb-4">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Buscar platos..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            data-testid="menu-search-input"
            className="pl-12 pr-4 py-3 rounded-xl bg-white border-gray-200"
          />
          {searchQuery && (
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-2 top-1/2 -translate-y-1/2"
              onClick={() => setSearchQuery("")}
            >
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>

        {/* Categories */}
        <ScrollArea className="w-full whitespace-nowrap">
          <div className="flex gap-2 pb-2">
            {categories.map((category) => (
              <Button
                key={category.category_id}
                variant={selectedCategory === category.category_id ? "default" : "outline"}
                size="sm"
                className={`rounded-full flex-shrink-0 ${selectedCategory === category.category_id ? 'bg-primary text-black' : ''}`}
                style={selectedCategory === category.category_id ? { backgroundColor: 'var(--primary)' } : {}}
                onClick={() => handleCategoryChange(category.category_id)}
                data-testid={`category-btn-${category.category_id}`}
              >
                {category.name}
              </Button>
            ))}
          </div>
          <ScrollBar orientation="horizontal" />
        </ScrollArea>
      </div>

      {/* Create Your Own Taco Banner */}
      {hasCustomTacoIngredients && (
        <div className="px-4 pb-2">
          <button
            onClick={() => navigate("/crea-tu-taco")}
            data-testid="create-your-taco-btn"
            className="w-full bg-gradient-to-r from-primary via-primary/80 to-primary/60 rounded-2xl p-4 flex items-center gap-4 shadow-lg hover:shadow-xl transition-all hover:scale-[1.02] active:scale-[0.98]"
            style={{ 
              background: `linear-gradient(to right, var(--primary), var(--primary)cc, var(--primary)99)`
            }}
          >
            <div className="w-14 h-14 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center">
              <ChefHat className="w-8 h-8 text-white" />
            </div>
            <div className="flex-1 text-left">
              <h3 className="font-heading font-bold text-lg text-white">
                CREA TU TACO
              </h3>
              <p className="text-white/80 text-sm">
                Personaliza cada ingrediente a tu gusto
              </p>
            </div>
            <Badge className="bg-white text-black font-bold hover:bg-white" style={{ color: 'var(--primary)' }}>
              NUEVO
            </Badge>
          </button>
        </div>
      )}

      {/* Menu Items */}
      <div className="px-4 py-4">
        {selectedCategory && (
          <div className="mb-4">
            <h2 className="font-heading text-xl font-bold">
              {getCategoryName(selectedCategory)}
            </h2>
            <p className="text-muted-foreground text-sm">
              {filteredItems.length} productos
            </p>
          </div>
        )}
        {filteredItems.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4 border border-gray-700">
              <span className="text-2xl">🍽️</span>
            </div>
            <p className="text-gray-400 font-medium">No se encontraron productos en esta sección</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 px-2">
            {filteredItems.map((item, index) => (
              <Link
                key={item.item_id}
                to={`/menu/${item.item_id}`}
                className="group relative bg-[#0a0a0a] rounded-3xl overflow-hidden border border-white/5 hover:border-primary/40 transition-all duration-500 shadow-2xl h-[140px] flex"
                style={{ 
                   animationDelay: `${index * 50}ms`,
                   borderColor: 'rgba(var(--primary-rgb, 255, 255, 255), 0.1)'
                }}
              >
                {/* Image or Premium Fallback */}
                {item.image ? (
                  <div className="w-[140px] h-full overflow-hidden relative flex-shrink-0">
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#0a0a0a] w-1/4 right-0" />
                  </div>
                ) : (
                  <div className="w-[140px] h-full bg-gradient-to-br from-gray-800 to-black flex items-center justify-center p-4 text-center border-r border-white/5 flex-shrink-0">
                    <span className="text-white/20 font-black text-[10px] uppercase tracking-widest leading-none rotate-[-90deg] whitespace-nowrap">
                      CONEXO SELECCIÓN
                    </span>
                  </div>
                )}

                {/* Content Area */}
                <div className="flex-1 p-5 flex flex-col justify-between min-w-0 bg-gradient-to-b from-white/[0.02] to-transparent">
                  <div className="space-y-1">
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="font-heading font-black text-white text-base md:text-lg leading-tight uppercase tracking-tight truncate group-hover:text-primary transition-colors" style={{ color: 'inherit' }}>
                        {item.name}
                      </h3>
                      {item.best_seller && (
                        <span className="bg-primary/10 text-primary text-[9px] font-black px-2 py-0.5 rounded-full border border-primary/20 uppercase tracking-widest" style={{ color: 'var(--primary)', borderColor: 'var(--primary)', backgroundColor: 'rgba(var(--primary-rgb), 0.1)' }}>
                          BEST
                        </span>
                      )}
                    </div>
                    <p className="text-gray-500 text-[11px] md:text-xs line-clamp-2 leading-relaxed font-medium pr-4">
                      {item.description || "Deliciosa selección de ingredientes frescos preparados con pasión artesana."}
                    </p>
                  </div>

                  <div className="flex items-center justify-between pt-2 border-t border-white/5 mt-2">
                    <div className="flex items-baseline gap-1">
                      <span className="text-primary font-black text-xl tracking-tighter" style={{ color: 'var(--primary)' }}>
                        {(parseFloat(item.price) || 0).toFixed(2)}
                      </span>
                      <span className="text-primary/70 text-[10px] font-bold" style={{ color: 'var(--primary)', opacity: 0.7 }}>€</span>
                    </div>

                    <div className="flex items-center gap-2">
                      {item.extras?.length > 0 && (
                        <span className="text-[9px] text-gray-500 font-bold uppercase tracking-widest bg-gray-800/50 px-2 py-1 rounded border border-white/5">
                          + MODIFICAR
                        </span>
                      )}
                      <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-black shadow-lg group-hover:scale-110 transition-transform" style={{ backgroundColor: 'var(--primary)', boxShadow: '0 0 15px var(--primary)4d' }}>
                        <span className="text-lg font-bold">+</span>
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Table Confirmation Dialog */}
      <Dialog open={showTableConfirmation} onOpenChange={setShowTableConfirmation}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Table2 className="w-6 h-6 text-green-600" />
              Confirma tu Mesa
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Current table display */}
            <div className="bg-green-50 border-2 border-green-200 rounded-xl p-4 text-center">
              <p className="text-sm text-gray-600 mb-1">Has escaneado el QR de:</p>
              <div className="flex items-center justify-center gap-2">
                <Table2 className="w-8 h-8 text-green-600" />
                <p className="text-3xl font-bold text-green-700">Mesa {tableFromQR}</p>
              </div>
            </div>

            {/* Warning message */}
            <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-800">
                <p className="font-semibold mb-1">¿Es correcta tu mesa?</p>
                <p className="text-xs">Tu pedido se enviará a esta mesa. Si escaneaste el código equivocado, puedes cambiarlo abajo.</p>
              </div>
            </div>

            {/* Change table option */}
            <div className="pt-2 border-t">
              <p className="text-sm font-medium text-gray-700 mb-2">¿Escaneaste el código equivocado?</p>
              <div className="flex gap-2">
                <Input
                  type="number"
                  placeholder="Número de mesa correcto"
                  value={changeTableNumber}
                  onChange={(e) => setChangeTableNumber(e.target.value)}
                  className="flex-1"
                  min="1"
                />
                <Button
                  onClick={handleChangeTable}
                  variant="outline"
                  disabled={!changeTableNumber}
                  className="whitespace-nowrap"
                >
                  Cambiar
                </Button>
              </div>
            </div>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={clearTableSelection}
              className="w-full sm:w-auto"
            >
              Cancelar
            </Button>
            <Button
              onClick={confirmTable}
              className="w-full sm:w-auto bg-green-600 hover:bg-green-700 text-white"
            >
              ✓ Confirmar Mesa {tableFromQR}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MobileLayout>
  );
}
